import { useCallback, useMemo } from 'react';
import { usePreviousDistinct } from 'react-use';
import { SlippageTolerance } from 'src/@types/types';
import useCustomInputWithPresets from 'src/components/TradeForm/hooks/useCustomInputWithPresets';
import useSlippageToleranceInputHandlers from 'src/components/TradeForm/SlippageToleranceInput/hooks/useSlippageToleranceInputHandlers';
import usePreviewOrder from 'src/components/TradeForm/hooks/usePreviewOrder';
import {
	SLIPPAGE_PRESETS_NON_MAJORS,
	SLIPPAGE_PRESETS_MAJORS,
} from 'src/constants/constants';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { isMajorMarket } from 'src/utils/market';
import { toNotional } from 'src/utils/formatting';
import {
	BASE_PRECISION_EXP,
	BigNum,
	PRICE_PRECISION_EXP,
	ZERO,
} from '@drift-labs/sdk';
import { useShallow } from 'zustand/react/shallow';
import useGetTickSizeForMarket, {
	getTickSizeDecimalPlaces,
} from 'src/hooks/useGetTickSizeForMarket';

interface Params {
	slippageToleranceFromProps?: SlippageTolerance;
	slippageValueFromProps?: number;
	updateHandler?: (value: SlippageTolerance) => void;
	hideDynamic?: boolean;
}

export default function useSlippageToleranceInput({
	slippageToleranceFromProps,
	slippageValueFromProps,
	updateHandler,
	hideDynamic,
}: Params = {}) {
	const [slippageTolerance, baseSizeStringValue, side, currentMarketId] =
		useDriftStore(
			useShallow((s) => [
				s.tradeForm.slippageTolerance,
				s.tradeForm.baseSizeStringValue,
				s.tradeForm.side,
				s.selectedMarket?.current?.marketId,
			])
		);

	// Determine the current value.
	// If a slippage tolerance is passed in via props, we should prefer its value if:
	//   a) `updateHandler` is also provided, as this implies the passed in value is
	//      controlled by it, and we would show stale data otherwise.
	//   Or...
	//   b) The value in the trade form store has yet to change (i.e. no distinct
	//      prev value). Until it changes, we know the form hasn't been interacted
	//      with and so we should continue showing the passed in /  initial value.
	const tradeFormPrevDistinctVal = usePreviousDistinct(slippageTolerance);
	const rawCurrentValue = (() => {
		if (
			slippageToleranceFromProps !== undefined &&
			(!tradeFormPrevDistinctVal || updateHandler)
		) {
			return slippageToleranceFromProps;
		}

		return slippageTolerance;
	})();

	// Get preset options first so we can use them for fallback
	const presetOptions =
		!updateHandler && (!currentMarketId || !isMajorMarket(currentMarketId))
			? SLIPPAGE_PRESETS_NON_MAJORS
			: SLIPPAGE_PRESETS_MAJORS;

	// If hideDynamic is true but the current value is 'dynamic',
	// use the first preset option instead
	const currentValue = (() => {
		if (hideDynamic && rawCurrentValue === 'dynamic') {
			return presetOptions[0];
		}
		return rawCurrentValue;
	})();

	const {
		customInputValue,
		isCustomInputFocused,
		currentValueSource,
		updateStateFromCustomInput,
		updateStateFromPreset,
		handleBlur,
		handleFocus,
	} = useCustomInputWithPresets({ currentValue: currentValue.toString() });

	const { handleCustomInputChange, handlePresetClick } =
		useSlippageToleranceInputHandlers({
			updateHandler,
			hideDynamic,
			updateStateFromCustomInput,
			updateStateFromPreset,
		});

	const getIsCurrentValueFromPreset = useCallback(
		(value: number) => {
			// In the settings modal, `currentValue` is a string, while in the trade
			// form it's a number, so we just normalize to number.
			return currentValueSource === 'preset' && value === Number(currentValue);
		},
		[currentValue, currentValueSource]
	);

	const isCurrentDynamic = currentValue === 'dynamic';

	// Calculate the current dynamic slippage for showing in the UI.
	const baseSize = BigNum.fromPrint(baseSizeStringValue, BASE_PRECISION_EXP);
	const previewOrderParams = usePreviewOrder();

	const dynamicSlippageString = useMemo(() => {
		if (!isCurrentDynamic) {
			return;
		}

		if (slippageValueFromProps) {
			return slippageValueFromProps.toFixed(2);
		}

		if (!previewOrderParams?.slippage || baseSize.lte(ZERO)) {
			return;
		}

		return previewOrderParams?.slippage?.toFixed(2);
	}, [previewOrderParams?.slippage, baseSize, isCurrentDynamic]);

	const getTickSizeForMarket = useGetTickSizeForMarket();
	const tickSize = currentMarketId
		? getTickSizeForMarket(currentMarketId)
		: undefined;
	const decimalPlaces = tickSize ? getTickSizeDecimalPlaces(tickSize) : 2;

	const auctionStartPrice = useMemo(() => {
		if (!previewOrderParams?.auctionStartPrice) {
			return;
		}

		return toNotional(
			BigNum.from(
				previewOrderParams?.auctionStartPrice ?? ZERO,
				PRICE_PRECISION_EXP
			).toNum(),
			decimalPlaces
		);
	}, [previewOrderParams?.auctionStartPrice, decimalPlaces]);

	const auctionEndPrice = useMemo(() => {
		if (!previewOrderParams?.auctionEndPrice) {
			return;
		}

		return toNotional(
			BigNum.from(
				previewOrderParams?.auctionEndPrice ?? ZERO,
				PRICE_PRECISION_EXP
			).toNum(),
			decimalPlaces
		);
	}, [previewOrderParams?.auctionEndPrice, decimalPlaces]);

	return {
		currentValue,
		currentValueSource,
		getIsCurrentValueFromPreset,
		isCurrentDynamic,
		customInputValue,
		isCustomInputFocused,
		handleCustomInputChange,
		handleBlur,
		handleFocus,
		handlePresetClick,
		presetOptions,
		dynamicSlippageString,
		auctionStartPrice,
		auctionEndPrice,
		side,
	};
}
